import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const AssignStudentDialog = ({ open, onClose, selectedTrainings  }) => {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem('userId');

  // Helper function to get authorization header
  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  // Fetch records from API when the dialog opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      axios
        .get(apiUrl+`/api/studentcourse/active/${userId}`, {
          headers: {
            'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
          },
        })
        .then((response) => {
          setStudents(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching student data:", error);
          setLoading(false);
        });
    }
  }, [open, userId]);

  // Handle selection change
  const handleSelectionChange = (event) => {
    setSelectedStudent(event.target.value);
  };

  // Handle submit action — assign the selected student to the training(s).
  const handleSubmit = async () => {
    if (!guardAction('action.training.assign')) return;
    if (!selectedStudent) return;
    const trainingIds = (selectedTrainings || [])
      .map((t) => (typeof t === 'string' ? t : (t && (t.trainingId || t.id))))
      .filter(Boolean);
    if (trainingIds.length === 0) { onClose(); return; }

    setSubmitting(true);
    try {
      await axios.post(
        `${apiUrl}/api/create`,
        { trainingIds, studentId: selectedStudent, userId },
        {
          headers: {
            'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
          },
        },
      );
      onClose();
    } catch (error) {
      console.error("Error assigning student to training:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Assign Student</DialogTitle>
      <DialogContent>
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
          </div>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="student-select-label">Select Student</InputLabel>
            <Select
              labelId="student-select-label"
              value={selectedStudent}
              label="Select Student"
              onChange={handleSelectionChange}
            >
              {students.map((student) => (
                <MenuItem key={student.studentId} value={student.studentId}>
                  {student.firstName} {student.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={submitting || !selectedStudent}>
          {submitting ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
};

export default AssignStudentDialog;
